import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Eye, EyeSlash, Leaf, Lock, Printer, UploadSimple } from "@phosphor-icons/react";
import IntakeWizard from "../components/IntakeWizard";
import { SignaturePad } from "../components/SignaturePad";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTION_SECTIONS = ["needs", "home_visit", "form1", "assessment", "medications", "gty"];
const SECTION_TITLES = {
  needs: "What they need help with", home_visit: "Their home", form1: "The important details",
  assessment: "Health & safety", medications: "Medications", gty: "Getting to know them (optional)",
};

const UPLOAD_KINDS = [
  ["discharge_instructions", "Recent discharge instructions"],
  ["med_list", "Medication list (photo or PDF)"],
  ["poa", "Power of Attorney"],
  ["other", "Other document"],
];

const INITIAL_SECTIONS = [
  ["emergency_medical", "Emergency Medical Treatment Authorization"],
  ["transportation", "Transportation Services Acknowledgment"],
  ["private_hiring", "Private/Direct Hiring Acknowledgment"],
];

const SECTION_REVIEW_TITLES = {
  needs: "What they need help with", home_visit: "Their home", form1: "The important details",
  assessment: "Health & safety", medications: "Medications", gty: "Getting to know them",
};

const REVIEW_SKIP = new Set(["submitted_at", "submitted_via", "client_submitted_at", "_filled_by", "_filled_role",
  "_med_file", "_oxy_smoke_warn", "_weapons_info", "_epi_info", "poa_document", "guided_answers", "entries"]);

const REVIEW_LABELS = {
  full_name: "Name", dob: "Date of birth", service_address: "Address", county: "County", primary_phone: "Phone",
  email: "Email", preferred_communication: "Preferred contact", preferred_language: "Language",
  has_legal_rep: "Legal representative", rep_type: "Representative type", rep_name: "Representative",
  rep_relationship: "Relationship", rep_phone: "Representative phone", rep_email: "Representative email",
  directs_own_care: "Directs own care", primary_contact_type: "First contact about care",
  ec_name: "Emergency contact", ec_relationship: "Emergency contact relationship", ec_phone: "Emergency phone", ec_email: "Emergency email",
  payer: "Who pays", mco_name: "Health plan", medicaid_id: "Medicaid ID", mco_member_id: "Member ID",
  coordinator_name: "Service coordinator", coordinator_phone: "Coordinator phone", coordinator_email: "Coordinator email",
  caregiver_referral: "Caregiver referral", referral_name: "Referral name", referral_phone: "Referral phone",
  referral_email: "Referral email", referral_notes: "Referral notes",
  nickname: "Goes by", help_areas: "Needs help with", mobility: "Mobility", transfer_assist: "Physical help needed",
  memory: "Memory", description: "In their words", care_goals: "Goals for care", informal_supports: "Informal supports", backup_plan: "Backup plan",
  lives_alone: "Lives alone", who_lives_in_home: "Who lives there", under_18: "Anyone under 18", home_type: "Home type",
  floor_level: "Floor", stairs_affecting_care: "Stairs", entry_instructions: "Getting in", lockbox_code: "Lockbox",
  parking_instructions: "Parking", pets: "Pets", pet_details: "Pet details", smoking_inside: "Smoking inside",
  oxygen_in_use: "Oxygen in use", oxygen_details: "Oxygen details", weapons_in_home: "Weapons in home",
  weapons_storage: "Weapon storage", private_rooms: "Off-limits", other_safety_concern: "Other safety concerns",
  _dx: "Health conditions", _dx_other: "Other conditions", _dx_dementia: "Wandering/sundowning", _dx_dementia_detail: "Dementia details",
  _dx_diabetes: "Uses insulin", _dx_diabetes_detail: "Insulin managed by", _dx_stroke: "Stroke effects", _dx_parkinsons: "Parkinson's notes",
  _food_allergy: "Food allergies", _food_allergy_detail: "Food allergy details", _med_allergy: "Medication allergies",
  _med_allergy_detail: "Med allergy details", _exposure_plan: "Exposure plan", _epi_pen: "EpiPen", _epi_pen_where: "EpiPen kept",
  recent_falls: "Recent falls", fall_details: "Fall details", recent_hospitalization: "Recent hospital stay",
  hospitalization_details: "Hospital stay details", mobility_status: "Gets around by", assistive_devices: "Equipment",
  cognitive_concerns: "Memory/thinking concerns", cognitive_details: "Memory details", behavioral_concerns: "Wellbeing concerns",
  behavioral_details: "Wellbeing details", communication_limitations: "Glasses/hearing/dentures", communication_details: "Communication details",
  other_health_info: "Other health info", physician_name: "Doctor", physician_phone: "Doctor's phone",
  hospice: "Hospice", hospice_provider: "Hospice provider", skilled_nursing: "Skilled nursing", skilled_nursing_provider: "Nursing provider",
  anything_else: "Anything else", source: "Medication list source", notes: "Notes",
};

const reviewLabel = (k) => REVIEW_LABELS[k] || k.replace(/^_+/, "").replace(/_/g, " ").replace(/^\w/, (m) => m.toUpperCase());
const fmtVal = (v) => Array.isArray(v) ? v.join(", ") : String(v);

function demoSignaturePng(name) {
  const c = document.createElement("canvas");
  c.width = 480; c.height = 160;
  const ctx = c.getContext("2d");
  ctx.strokeStyle = "#1a1a2e"; ctx.lineWidth = 2.5; ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(30, 100);
  for (let x = 30; x < 450; x += 6) ctx.lineTo(x, 100 + Math.sin(x / 14) * 26 * Math.sin(x / 47));
  ctx.stroke();
  ctx.font = "italic 26px Georgia"; ctx.fillStyle = "#1a1a2e";
  ctx.fillText(name, 44, 136);
  return c.toDataURL("image/png");
}

export default function IntakePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [phase, setPhase] = useState("questions"); // questions | uploads | signing | done
  const [secIdx, setSecIdx] = useState(0);
  const [sigIdx, setSigIdx] = useState(0);
  const isDemo = new URLSearchParams(window.location.search).has("demo");
  const [signName, setSignName] = useState("");
  const [signRel, setSignRel] = useState("");
  const [sig, setSig] = useState(null);
  const [showDoc, setShowDoc] = useState(false);
  const [agrInitials, setAgrInitials] = useState({});
  const [upFile, setUpFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [offerDismissed, setOfferDismissed] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractResult, setExtractResult] = useState(null);
  const [extractNote, setExtractNote] = useState("");
  const [returnToReview, setReturnToReview] = useState(false);
  const [nameResolved, setNameResolved] = useState(false);
  const navInit = useRef(false);

  const sectionDone = useCallback((data) => {
    const st = data.steps || {};
    return {
      needs: !!st.needs, home_visit: !!st.home_visit, form1: !!data.form1_submitted,
      assessment: !!st.assessment, medications: !!st.medications, gty: !!st.gty,
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${BASE}/intake/${token}`);
      setInfo(r.data);
      if (!navInit.current) {
        navInit.current = true;
        const done = sectionDone(r.data);
        const firstOpen = QUESTION_SECTIONS.findIndex((k) => !done[k]);
        if (firstOpen >= 0) {
          setSecIdx(firstOpen);
        } else {
          setPhase("uploads");
        }
        if (r.data.intake_meta?.completer_name) setSignName(r.data.intake_meta.completer_name);
        if (r.data.intake_meta?.completer_role === "Power of Attorney / legal representative") setSignRel("POA");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "This link is invalid or has expired.");
    }
  }, [token, sectionDone]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <p className="max-w-md rounded-lg border border-stone-200 bg-white p-6 text-center text-sm text-stone-600" data-testid="intake-error">{error}</p>
      </div>
    );
  }
  if (!info) {
    return <div className="flex min-h-screen items-center justify-center bg-background"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  const meta = info.intake_meta || {};
  const doneMap = sectionDone(info);
  const nothingDoneYet = QUESTION_SECTIONS.every((k) => !doneMap[k]);
  const audience = meta.completer_role === "The client themselves" ? "client"
    : meta.completer_role === "A Silver Home Care office staff member" ? "staff" : "family";

  // signing document sequence: consent first, then acknowledgements, then agreement (if the office prepared it)
  const signDocs = [
    { key: "phi_consent", type: "consent", title: "Authorization & Consent",
      desc: "Authorizes electronic storage of your information and the use of electronic signatures.",
      signed: info.consent_signed },
    ...(info.acknowledgements || []).map((a) => ({ ...a, type: "ack" })),
    ...(info.agreement_ready ? [{ key: "agreement", type: "agreement", title: "Service Agreement & Authorization for Care",
      desc: "The agreement the office prepared, including the rates and the three authorization sections.",
      signed: info.agreement_signed }] : []),
  ];
  const currentDoc = signDocs[Math.min(sigIdx, signDocs.length - 1)];
  const allSigned = signDocs.every((d) => d.signed);

  const saveSection = async (key, data) => {
    try {
      if (key === "form1") await axios.post(`${BASE}/intake/${token}/form1`, { data });
      else await axios.post(`${BASE}/intake/${token}/step/${key}`, { data });
      await load();
      if (returnToReview) {
        setReturnToReview(false);
        setPhase("review");
      } else if (secIdx < QUESTION_SECTIONS.length - 1) setSecIdx(secIdx + 1);
      else setPhase("uploads");
    } catch (e) {
      alert(e?.response?.data?.detail || "Saving failed — please try again.");
    }
  };

  const runExtract = async (file) => {
    if (!file) return;
    setExtracting(true);
    setExtractNote("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${BASE}/intake/${token}/extract`, fd);
      setExtractResult(r.data);
      await load();
      return r.data;
    } catch (e) {
      const msg = e?.response?.data?.detail || "That file couldn't be read — you can still answer the questions by hand.";
      setExtractNote(msg);
      return null;
    } finally {
      setExtracting(false);
    }
  };

  const goBackToSection = (idx) => {
    setSecIdx(idx);
    setReturnToReview(true);
    setPhase("questions");
  };

  const useFoundName = async () => {
    try {
      await axios.post(`${BASE}/intake/${token}/client-name`, { full_name: extractResult.found_name });
      setNameResolved(true);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't update the name — you can fix it in the questions.");
    }
  };

  const enterSigning = async () => {
    await load();
    const first = signDocs.findIndex((d) => !d.signed);
    setSigIdx(first >= 0 ? first : 0);
    setPhase("signing");
  };

  const signCurrent = async (demo) => {
    const name = signName.trim();
    if (!name) { alert("Please type your full name"); return; }
    const png = demo ? demoSignaturePng(name) : sig;
    if (!png) { alert("Please draw your signature"); return; }
    if (currentDoc.type === "agreement" && INITIAL_SECTIONS.some(([k]) => !(agrInitials[k] || "").trim())) {
      alert("Please initial all three authorization sections"); return;
    }
    setBusy(true);
    try {
      const body = { signer_name: name, relationship: signRel.trim(), signature_png: png, entries: [] };
      if (currentDoc.type === "consent") await axios.post(`${BASE}/intake/${token}/sign-consent`, body);
      else if (currentDoc.type === "agreement") await axios.post(`${BASE}/intake/${token}/sign-agreement`, { ...body, initials: agrInitials });
      else await axios.post(`${BASE}/intake/${token}/sign/${currentDoc.key}`, body);
      setSig(null);
      setShowDoc(false);
      const r = await axios.get(`${BASE}/intake/${token}`);
      setInfo(r.data);
      const nextDocs = [
        { signed: r.data.consent_signed },
        ...(r.data.acknowledgements || []),
        ...(r.data.agreement_ready ? [{ signed: r.data.agreement_signed }] : []),
      ];
      const next = nextDocs.findIndex((d) => !d.signed);
      if (next >= 0) setSigIdx(next);
      else if (!r.data.agreement_ready && !r.data.agreement_signed) setPhase("signing");
      else setPhase("done");
    } catch (e) {
      alert(e?.response?.data?.detail || "Signing failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const uploadDoc = async () => {
    if (!upFile) { alert("Choose a file or photo first"); return; }
    const file = upFile;
    setUpFile(null);
    const r = await runExtract(file);
    if (r) {
      setExtractNote(r.total_filled > 0
        ? `Read the ${r.kind_label} — filled ${r.total_filled} new answer${r.total_filled === 1 ? "" : "s"}. Anything already answered was left alone.`
        : `Saved the ${r.kind_label} — nothing new to pull from it.`);
    } else {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(`${BASE}/intake/${token}/upload?kind=other`, fd);
        setExtractNote("We couldn't read that one automatically, but the document is saved to the file.");
        await load();
      } catch {
        setExtractNote("Upload failed — please try again.");
      }
    }
  };

  const reviewRows = (key) => {
    let data = key === "form1" ? { ...(info.responses?.form1 || {}), ...(info.form1 || {}) } : (info.responses?.[key] || {});
    if (key === "medications") {
      const rows = (data.entries || []).filter((e) => e.name)
        .map((e) => ["Medication", [e.name, e.dosage, e.frequency].filter(Boolean).join(" — ")]);
      if (data.notes) rows.push(["Notes", data.notes]);
      if (data.source) rows.push(["Source", data.source]);
      return rows;
    }
    if (key === "gty") {
      const rows = Object.values(data.guided_answers || {}).map((g) => [g.question, g.answer]);
      if (data.anything_else) rows.push([reviewLabel("anything_else"), data.anything_else]);
      return rows;
    }
    return Object.entries(data)
      .filter(([k, v]) => !REVIEW_SKIP.has(k) && v !== "" && v !== null && !(Array.isArray(v) && !v.length))
      .map(([k, v]) => [reviewLabel(k), fmtVal(v)]);
  };

  const progressText = phase === "questions"
    ? `Questions — part ${secIdx + 1} of ${QUESTION_SECTIONS.length} · ${SECTION_TITLES[QUESTION_SECTIONS[secIdx]]}`
    : phase === "uploads" ? "Documents (optional)"
    : phase === "review" ? "Review — check everything before signing"
    : phase === "signing" ? `Signing — document ${Math.min(sigIdx + 1, signDocs.length)} of ${signDocs.length}`
    : "Complete";

  const wizardFor = (key) => {
    const common = { mode: "family", audience, clientName: info.form1?.full_name || "the client", demo: isDemo, onSave: (d) => saveSection(key, d) };
    if (key === "needs") return <IntakeWizard sectionKey="needs" {...common} askFiller welcome
      initial={{ ...(info.responses?.needs || {}), _filled_by: meta.completer_name || "", _filled_role: meta.completer_role || "" }} />;
    if (key === "home_visit") return <IntakeWizard sectionKey="home_visit" {...common} initial={info.responses?.home_visit || {}} />;
    if (key === "form1") return <IntakeWizard sectionKey="form1" {...common} initial={{ ...(info.responses?.form1 || {}), ...(info.form1 || {}) }} />;
    if (key === "assessment") return <IntakeWizard sectionKey="assessment" {...common} initial={info.responses?.assessment || {}} />;
    if (key === "medications") return <IntakeWizard sectionKey="medications" {...common} initial={info.responses?.medications || {}}
      onUploadFile={async (file) => { const fd = new FormData(); fd.append("file", file); await axios.post(`${BASE}/intake/${token}/upload?kind=med_list`, fd); }} />;
    return <IntakeWizard sectionKey="gty" {...common} initial={info.responses?.gty || {}} />;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="intake-page">
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex h-14 max-w-3xl items-center gap-2 px-6">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Leaf size={18} weight="fill" /></span>
          <span className="font-heading text-lg font-semibold tracking-tight text-stone-900">Silver Home Care <span className="font-normal text-stone-500">— New Client Intake</span></span>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-6 py-8">
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900" data-testid="intake-welcome">
          Welcome{info.client_name ? `, ${info.client_name.split(" ")[0]}` : ""}
        </h1>
        <p className="mt-1 text-sm text-stone-500" data-testid="intake-progress">{progressText} · saves as you go · link expires {String(info.expires_at).slice(0, 10)}</p>

        {isDemo && (
          <p className="mt-4 rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 px-4 py-2.5 text-xs text-[#8a5d14]" data-testid="intake-demo-note">
            <strong>Demo mode</strong> — every question shows up already answered; just tap <strong>Continue</strong> to walk the whole conversation to the end. At the signing step, use the one-tap demo sign.
          </p>
        )}

        {phase === "questions" && !returnToReview && !isDemo && nothingDoneYet && (info.prefills || []).length === 0 && !offerDismissed && !extractResult && (
          <div className="mt-6 rounded-xl border border-stone-200 bg-white p-8 shadow-sm sm:p-10" data-testid="intake-paperwork-offer">
            <h2 className="font-heading text-2xl font-semibold text-stone-900">Got paperwork? Tell me about it first.</h2>
            <p className="mt-2 text-base leading-relaxed text-stone-600">
              If you have discharge instructions, a medication list, a face sheet, or anything else — upload it and we'll
              <strong> read it and pre-fill as many answers as we can</strong>. You'll still see every question and can change anything.
            </p>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-end">
              <input type="file" accept="image/*,.pdf,.txt" onChange={(e) => setUpFile(e.target.files?.[0] || null)}
                className="flex-1 text-sm text-stone-600" data-testid="intake-extract-input" />
              <Button onClick={async () => { const f = upFile; setUpFile(null); await runExtract(f); }} disabled={extracting || !upFile}
                className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="intake-extract-submit">
                {extracting ? "Reading it…" : "Read it & pre-fill"}
              </Button>
            </div>
            {extractNote && <p className="mt-3 text-sm font-medium text-[#C0392B]" data-testid="intake-extract-error">{extractNote}</p>}
            <button type="button" onClick={() => setOfferDismissed(true)}
              className="mt-4 text-sm font-medium text-stone-400 hover:text-stone-600 hover:underline" data-testid="intake-offer-skip">
              No paperwork — start the questions
            </button>
          </div>
        )}

        {phase === "questions" && !returnToReview && extractResult && (
          <div className="mt-6 rounded-xl border border-stone-200 bg-white p-8 shadow-sm sm:p-10" data-testid="intake-extract-summary">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">{extractResult.kind_label}</p>
            <h2 className="mt-2 font-heading text-2xl font-semibold text-stone-900">Here's what we found</h2>
            {extractResult.found_name && !nameResolved
              && extractResult.found_name.trim().toLowerCase() !== (info.client_name || "").trim().toLowerCase() && (
              <div className="mt-4 rounded-lg border border-[#B7791F]/40 bg-[#B7791F]/5 p-4" data-testid="intake-name-choice">
                <p className="text-sm font-semibold text-[#8a5d14]">The name on this document doesn't match what was entered</p>
                <p className="mt-1 text-sm text-stone-700">
                  Document says <strong>{extractResult.found_name}</strong> — this intake was started for <strong>{info.client_name || "(no name)"}</strong>.
                </p>
                <div className="mt-3 flex flex-wrap gap-3">
                  <Button onClick={useFoundName} className="bg-[#B7791F] text-white hover:bg-[#8a5d14]" data-testid="intake-name-use-found">
                    Use {extractResult.found_name}
                  </Button>
                  <button type="button" onClick={() => setNameResolved(true)}
                    className="text-sm font-medium text-stone-500 hover:underline" data-testid="intake-name-keep-current">
                    Keep {info.client_name || "the current name"}
                  </button>
                </div>
              </div>
            )}
            {extractResult.summary?.length > 0 && (
              <ul className="mt-4 space-y-2">
                {extractResult.summary.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm leading-relaxed text-stone-700" data-testid={`intake-extract-bullet-${i}`}>
                    <CheckCircle size={15} weight="fill" className="mt-0.5 shrink-0 text-[#1e8449]" /> {s}
                  </li>
                ))}
              </ul>
            )}
            {extractResult.total_filled > 0 && (
              <div className="mt-4 flex flex-wrap gap-2" data-testid="intake-extract-counts">
                {Object.entries(extractResult.filled_counts || {}).map(([k, n]) => (
                  <span key={k} className="rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
                    {n} {SECTION_REVIEW_TITLES[k] || k} answer{n === 1 ? "" : "s"}
                  </span>
                ))}
              </div>
            )}
            {extractResult.extra_info && (
              <div className="mt-4 rounded-lg border border-stone-200 bg-stone-50 p-4" data-testid="intake-extract-extra">
                <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Also kept for the office</p>
                <p className="mt-1 text-sm leading-relaxed text-stone-600">{extractResult.extra_info}</p>
              </div>
            )}
            <p className="mt-4 text-sm text-stone-500">Nothing is final — each question will show what we found, and you can change any answer as you go.</p>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <Button onClick={() => { setExtractResult(null); setOfferDismissed(true); }}
                className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="intake-extract-start">
                Start the questions — check each answer
              </Button>
              <button type="button" onClick={() => setExtractResult(null)}
                className="text-sm font-medium text-stone-400 hover:text-stone-600 hover:underline" data-testid="intake-extract-another">
                Upload another document
              </button>
            </div>
          </div>
        )}

        {phase === "questions" && (returnToReview || isDemo || !nothingDoneYet || (info.prefills || []).length > 0 || offerDismissed) && (
          <div className="mt-6" key={QUESTION_SECTIONS[secIdx]} data-testid="intake-section">
            {wizardFor(QUESTION_SECTIONS[secIdx])}
            {QUESTION_SECTIONS[secIdx] === "gty" && !returnToReview && (
              <div className="mt-3 text-right">
                <button type="button" onClick={() => setPhase("uploads")} className="text-xs font-medium text-stone-400 hover:text-stone-600 hover:underline" data-testid="intake-gty-skip">
                  Skip this section →
                </button>
              </div>
            )}
          </div>
        )}

        {phase === "uploads" && (
          <div className="mt-6 space-y-4" data-testid="intake-uploads">
            <div className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm">
              <h2 className="font-heading text-lg font-semibold text-stone-900">Anything to share? (optional)</h2>
              <p className="mt-1 text-sm leading-relaxed text-stone-500">
                Upload discharge instructions, a medication list, Power of Attorney paperwork, or anything else — we'll figure out what it is,
                <strong> pull out any answers that are still missing</strong>, and keep the document on file. Photos from your phone are fine.
              </p>
            </div>
            <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
              <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                <div className="space-y-1.5">
                  <Label>File or photo</Label>
                  <input type="file" accept="image/*,.pdf,.txt" onChange={(e) => setUpFile(e.target.files?.[0] || null)}
                    className="w-full text-sm text-stone-600" data-testid="intake-upload-input" />
                </div>
                <Button onClick={uploadDoc} disabled={extracting || !upFile} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="intake-upload-submit">
                  <UploadSimple size={15} className="mr-1.5" /> {extracting ? "Reading it…" : "Upload & read it"}
                </Button>
              </div>
              {extractNote && <p className="mt-3 text-sm font-medium text-stone-600" data-testid="intake-upload-note">{extractNote}</p>}
              {(info.uploads || []).length > 0 && (
                <div className="mt-4 space-y-1.5 border-t border-stone-100 pt-3" data-testid="intake-uploads-list">
                  {info.uploads.map((u) => (
                    <p key={u.id} className="flex items-center gap-2 text-sm text-stone-600" data-testid={`intake-upload-${u.id}`}>
                      <CheckCircle size={15} weight="fill" className="text-[#1e8449]" />
                      {(UPLOAD_KINDS.find(([k]) => k === u.kind) || [])[1] || u.kind} — {u.filename}
                    </p>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end rounded-lg border border-stone-200 bg-white p-4 shadow-sm">
              <Button onClick={() => setPhase("review")} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="intake-uploads-continue">
                {(info.uploads || []).length ? "Continue to review" : "Skip — continue to review"}
              </Button>
            </div>
          </div>
        )}

        {phase === "review" && (
          <div className="mt-6 space-y-4" data-testid="intake-review">
            <div className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm">
              <h2 className="font-heading text-lg font-semibold text-stone-900">One last look before signing</h2>
              <p className="mt-1 text-sm leading-relaxed text-stone-500">
                Here's everything you've told us, section by section. If anything's wrong, tap <strong>Change</strong> on that section —
                you'll step back through just those questions, fix it, and land right back here.
              </p>
            </div>
            {QUESTION_SECTIONS.map((key, idx) => {
              const rows = reviewRows(key);
              return (
                <div key={key} className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid={`intake-review-${key}`}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-heading text-sm font-semibold text-stone-900">{SECTION_REVIEW_TITLES[key]}</p>
                    <button type="button" onClick={() => goBackToSection(idx)}
                      className="text-xs font-semibold text-primary hover:underline" data-testid={`intake-review-change-${key}`}>
                      Change
                    </button>
                  </div>
                  {rows.length === 0 ? (
                    <p className="mt-2 text-sm italic text-stone-400">Nothing answered in this section.</p>
                  ) : (
                    <dl className="mt-3 space-y-1.5">
                      {rows.map(([label, value], i) => (
                        <div key={i} className="grid gap-1 sm:grid-cols-[180px_1fr]">
                          <dt className="text-xs font-medium text-stone-400">{label}</dt>
                          <dd className="text-sm leading-relaxed text-stone-700">{value}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>
              );
            })}
            {(info.prefills || []).some((p) => p.extra_info) && (
              <div className="rounded-lg border border-stone-200 bg-stone-50 p-4" data-testid="intake-review-extra">
                <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Also kept from your documents</p>
                {info.prefills.filter((p) => p.extra_info).map((p) => (
                  <p key={p.id} className="mt-1 text-sm leading-relaxed text-stone-600">{p.extra_info}</p>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-stone-200 bg-white p-4 shadow-sm">
              <button type="button" onClick={() => window.open(`${BASE}/intake/${token}/packet/pdf`, "_blank")}
                className="inline-flex h-9 items-center gap-1.5 rounded-md border border-stone-300 px-4 text-sm font-medium text-stone-700 hover:bg-stone-900 hover:text-white"
                data-testid="intake-print-packet">
                <Printer size={15} /> Print the whole packet
              </button>
              <Button onClick={enterSigning} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="intake-review-continue">
                Everything looks right — continue to signing
              </Button>
            </div>
          </div>
        )}

        {phase === "signing" && !allSigned && currentDoc && (
          <div className="mt-6 space-y-4" data-testid="intake-signing">
            <div className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm">
              <h2 className="font-heading text-lg font-semibold text-stone-900">Last step — review & sign</h2>
              <p className="mt-1 text-sm leading-relaxed text-stone-500">
                The questions are done. Each document appears below, one at a time — review it, type your name, draw your signature, and continue.
              </p>
            </div>
            <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid={`intake-sign-card-${currentDoc.key}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-sm font-semibold text-stone-900">{currentDoc.title}</p>
                  <p className="mt-0.5 text-xs text-stone-500">{currentDoc.desc}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {currentDoc.type !== "agreement" && (
                    <button onClick={() => setShowDoc(!showDoc)}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-stone-300 px-3 text-xs font-medium text-stone-700 hover:bg-stone-900 hover:text-white"
                      data-testid={`intake-sign-view-${currentDoc.key}`}>
                      {showDoc ? <EyeSlash size={14} /> : <Eye size={14} />} {showDoc ? "Hide document" : "Review document"}
                    </button>
                  )}
                  <button type="button"
                    onClick={() => window.open(currentDoc.type === "agreement" ? `${BASE}/intake/${token}/agreement/pdf` : `${BASE}/intake/${token}/template/${currentDoc.key}`, "_blank")}
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border border-stone-300 px-3 text-xs font-medium text-stone-700 hover:bg-stone-900 hover:text-white"
                    data-testid={`intake-sign-print-${currentDoc.key}`}>
                    <Printer size={14} /> Print
                  </button>
                </div>
              </div>

              {(showDoc && currentDoc.type !== "agreement") && (
                <iframe src={`${BASE}/intake/${token}/template/${currentDoc.key}`} title={currentDoc.title}
                  className="mt-4 h-[420px] w-full rounded-lg border border-stone-200 bg-white" data-testid={`intake-sign-iframe-${currentDoc.key}`} />
              )}

              {currentDoc.type === "agreement" && (
                <>
                  <iframe src={`${BASE}/intake/${token}/agreement/pdf`} title="Service Agreement"
                    className="mt-4 h-[480px] w-full rounded-lg border border-stone-200 bg-white" data-testid="intake-agreement-iframe" />
                  <div className="mt-4 rounded-lg border border-stone-200 bg-stone-50 p-4" data-testid="intake-agreement-initials">
                    <p className="font-heading text-sm font-semibold text-stone-900">Initial each authorization</p>
                    <div className="mt-3 space-y-2.5">
                      {INITIAL_SECTIONS.map(([k, label]) => (
                        <div key={k} className="flex items-center justify-between gap-3">
                          <p className="text-sm text-stone-600">{label}</p>
                          <Input value={agrInitials[k] || ""} maxLength={4} placeholder="Initials"
                            onChange={(e) => setAgrInitials({ ...agrInitials, [k]: e.target.value })}
                            className="h-9 w-24 border-stone-300 bg-white text-center" data-testid={`intake-agr-initials-${k}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div className="mt-4 grid grid-cols-1 gap-4 border-t border-stone-100 pt-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Your full name *</Label>
                  <Input value={signName} onChange={(e) => setSignName(e.target.value)} className="border-stone-300 bg-white" data-testid={`intake-sign-name-${currentDoc.key}`} />
                </div>
                <div className="space-y-1.5">
                  <Label>Relationship (if signing for the client)</Label>
                  <Input value={signRel} onChange={(e) => setSignRel(e.target.value)} className="border-stone-300 bg-white" placeholder="e.g. Daughter, POA" data-testid={`intake-sign-rel-${currentDoc.key}`} />
                </div>
              </div>
              <div className="mt-4">
                <SignaturePad onChange={setSig} />
              </div>
              <div className="mt-4 flex items-center justify-between">
                {isDemo ? (
                  <button type="button" onClick={() => signCurrent(true)} disabled={busy}
                    className="text-xs font-semibold text-[#8a5d14] hover:underline" data-testid={`intake-demo-sign-${currentDoc.key}`}>
                    Demo: one-tap sign as {signName || "the completer"}
                  </button>
                ) : <span />}
                <Button onClick={() => signCurrent(false)} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`intake-sign-submit-${currentDoc.key}`}>
                  {busy ? "Signing..." : sigIdx === signDocs.length - 1 ? "Sign & Finish" : "Sign & Continue"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {phase === "signing" && allSigned && (
          <div className="mt-6">
            {!info.agreement_ready && !info.agreement_signed ? (
              <div className="rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-5" data-testid="intake-agreement-pending">
                <p className="text-sm font-semibold text-[#8a5d14]">Everything is signed — one last document is coming</p>
                <p className="mt-1 text-sm text-stone-600">The office is still preparing the Service Agreement (it includes the rates). We'll send this link back to you when it's ready to sign.</p>
                <div className="mt-3 flex justify-end">
                  <Button onClick={() => setPhase("done")} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="intake-agreement-skip">Finish for now</Button>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-[#27AE60]/30 bg-[#27AE60]/5 p-5 text-center">
                <p className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1e8449]"><CheckCircle size={16} weight="fill" /> All documents signed</p>
                <div className="mt-3"><Button onClick={() => setPhase("done")} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="intake-signing-done">Finish</Button></div>
              </div>
            )}
          </div>
        )}

        {phase === "done" && (
          <div className="mt-6 rounded-lg border border-[#27AE60]/30 bg-[#27AE60]/5 p-8 text-center shadow-sm" data-testid="intake-done">
            <CheckCircle size={40} weight="fill" className="mx-auto text-[#1e8449]" />
            <h2 className="mt-3 font-heading text-xl font-semibold text-stone-900">All set — thank you!</h2>
            <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-stone-600">
              Silver Home Care has received your answers, information, and signatures. The team will review everything, prepare the care plan, and then send it to you to review and sign.
            </p>
            <button type="button" onClick={() => window.open(`${BASE}/intake/${token}/packet/pdf`, "_blank")}
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" data-testid="intake-done-print">
              <Printer size={15} /> Print or save a copy of the packet
            </button>
            {info.agreement_ready && !info.agreement_signed && (
              <Button onClick={() => setPhase("signing")} variant="outline" className="mt-4 border-stone-300" data-testid="intake-done-sign-agreement">
                Go back to sign the Service Agreement
              </Button>
            )}
          </div>
        )}

        <p className="mt-8 flex items-center justify-center gap-1.5 text-xs text-stone-400">
          <Lock size={12} /> Your information is stored securely and only shared with your care team.
        </p>
      </main>
    </div>
  );
}
